/**
 * EAD feature map labels — format only. Version and revision come from the server.
 */

export function formatEadFeatureMapLabel(
  generation: number,
  revision: number,
  options?: { finalized?: boolean; pending?: boolean },
): string {
  if (options?.pending) {
    const g = Math.max(0, generation);
    return g > 0 ? `EAD-feature map V${g} pending` : "EAD-feature map pending";
  }
  const g = Math.max(0, generation);
  if (g <= 0) return "EAD-feature map pending";
  const rev = Math.max(0, revision);
  if (options?.finalized || rev <= 0) return `EAD-feature map V${g}`;
  return `EAD-feature map V${g} (Rev ${rev})`;
}

/** Build label from server-provided execution fields only (no client-side version math). */
export function formatEadFeatureMapLabelFromServer(ex: {
  pfmGenerationVersion?: number;
  pfmRevision?: number;
  pfmFinalized?: boolean;
  pfmSnapshotPhase?: string;
}): string {
  const generation = Math.max(0, Number(ex.pfmGenerationVersion ?? 0));
  const revision = Math.max(0, Number(ex.pfmRevision ?? 0));
  const phase = String(ex.pfmSnapshotPhase || "").trim();

  if (ex.pfmFinalized === true) {
    return formatEadFeatureMapLabel(generation, revision, { finalized: true });
  }
  if (phase === "baseline_preview") {
    return formatEadFeatureMapLabel(generation, 0, { pending: true });
  }
  if (phase === "finalizing") {
    return `${formatEadFeatureMapLabel(generation, revision)} — finalizing`;
  }
  return formatEadFeatureMapLabel(generation, revision);
}

function firstPositiveNumber(...values: Array<number | undefined | null>): number {
  for (const value of values) {
    const n = Math.max(0, Number(value ?? 0));
    if (n > 0) return n;
  }
  return 0;
}

function formatPfmMapVersionRev(runNumber: number, revision: number): string {
  const run = Math.max(0, runNumber);
  const rev = Math.max(0, revision);
  if (run <= 0) return "";
  return rev > 0 ? `V ${run} (Rev ${rev})` : `V ${run}`;
}

/** Normalize server lineage fields (camel or snake) for mindmap headers. */
export function resolvePfmMindmapLineage(ex: {
  runNumber?: number;
  pfmGenerationVersion?: number;
  pfmHasCommittedSnapshot?: boolean;
  hasDeliveredPfmMapReport?: boolean;
  pfmMapDisplayMode?: "current" | "previous" | string;
  pfmRevision?: number;
  pfmStepVersion?: number;
  pfmSourceRunNumber?: number;
  pfmBaselineTreeVersion?: number;
  pfmSnapshotPhase?: string;
  currentRunNumber?: number;
  currentPfmRevision?: number;
  previousPfmRunNumber?: number;
  previousPfmRevision?: number;
}): {
  hasCurrentPfmMap: boolean;
  currentRunNumber: number;
  currentPfmRevision: number;
  previousPfmRunNumber: number;
  previousPfmRevision: number;
  lineageReady: boolean;
} {
  const mode = String(ex.pfmMapDisplayMode || "").trim().toLowerCase();
  const phase = String(ex.pfmSnapshotPhase || "").trim();

  // Simple data-driven logic: Check if current run has PFM nodes in DB
  // If pfmRevision > 0, it means current run has committed PFM snapshot
  const hasCurrentPfmNodes = firstPositiveNumber(
    ex.currentPfmRevision,
    ex.pfmRevision,
    ex.pfmStepVersion,
  ) > 0;

  const hasCurrent =
    mode === "current"
    || hasCurrentPfmNodes;
  const currentRun = firstPositiveNumber(
    ex.currentRunNumber,
    ex.runNumber,
    ex.pfmGenerationVersion,
  );
  const currentRev = firstPositiveNumber(
    ex.currentPfmRevision,
    ex.pfmRevision,
    ex.pfmStepVersion,
  );
  const previousRun = firstPositiveNumber(
    ex.previousPfmRunNumber,
    ex.pfmSourceRunNumber,
    ex.pfmBaselineTreeVersion,
  );
  const previousRev = firstPositiveNumber(
    ex.previousPfmRevision,
    ex.pfmRevision,
    ex.pfmStepVersion,
  );
  const lineageReady =
    hasCurrent
    || previousRun > 0
    || mode === "previous"
    || phase === "baseline_preview";
  return {
    hasCurrentPfmMap: hasCurrent,
    currentRunNumber: currentRun,
    currentPfmRevision: currentRev,
    previousPfmRunNumber: previousRun,
    previousPfmRevision: previousRev,
    lineageReady,
  };
}

/** Minimal run row for client-side V/Rev (matches executions + execution_pfm_artifacts index). */
export type PfmLineageRunRef = {
  id?: string;
  runNumber?: number;
  pfmRevision?: number;
  pfmHasCommittedSnapshot?: boolean;
  hasDeliveredPfmMapReport?: boolean;
  validForDataReportingTraining?: boolean;
  contributesToLearning?: boolean;
  status?: string;
};

function runEligibleForOperatorBaseline(run: PfmLineageRunRef): boolean {
  if (run.hasDeliveredPfmMapReport !== true) return false;
  if (run.validForDataReportingTraining === false) return false;
  if (run.contributesToLearning === false) return false;
  return runHasCommittedTree(run);
}

function runById(runs: PfmLineageRunRef[], id: string): PfmLineageRunRef | undefined {
  const want = String(id || "").trim();
  if (!want) return undefined;
  return runs.find((r) => String(r.id || "").trim() === want);
}

function runHasCommittedTree(run: PfmLineageRunRef): boolean {
  if (run.pfmHasCommittedSnapshot === true) return true;
  if (Math.max(0, Number(run.pfmRevision || 0)) > 0) return true;
  const st = String(run.status || "").trim().toLowerCase();
  return (
    (st === "completed" || st === "failed" || st === "cancelled" || st === "error")
    && Math.max(0, Number(run.runNumber || 0)) > 0
  );
}

/** Highest run_number on the template with a delivered, eligible committed pfm-tree. */
export function findLatestTemplatePfmTreeRun(
  templateRuns: PfmLineageRunRef[],
  excludeExecutionId = "",
): PfmLineageRunRef | undefined {
  const exclude = String(excludeExecutionId || "").trim();
  return templateRuns
    .filter((run) => {
      const rid = String(run.id || "").trim();
      if (!rid || (exclude && rid === exclude)) return false;
      return runEligibleForOperatorBaseline(run);
    })
    .sort((a, b) => Number(b.runNumber || 0) - Number(a.runNumber || 0))[0];
}

/**
 * Resolve V/Rev on the client — same rules as the DB:
 * - Own map: V = this run_number, Rev = this pfm_revision
 * - Else: template-wide most recent pfm-tree on the parent template
 */
export function resolveClientPfmLineage(
  ex: Parameters<typeof resolvePfmMindmapLineage>[0] & {
    id?: string;
    linkedTemplateId?: string;
    inheritedFromExecutionId?: string;
    pfmBaselineExecutionId?: string;
    pfmTreeReadExecutionId?: string;
  },
  templateRuns: PfmLineageRunRef[] = [],
  opts?: {
    baselineExecutionId?: string;
    inheritedExecutionId?: string;
    treeReadExecutionId?: string;
  },
): ReturnType<typeof resolvePfmMindmapLineage> {
  const selfId = String(ex.id || "").trim();
  const currentRn = Math.max(
    0,
    Number(ex.runNumber || ex.pfmGenerationVersion || ex.currentRunNumber || 0),
  );

  const mode = String(ex.pfmMapDisplayMode || "").trim().toLowerCase();
  const ownRev = Math.max(
    0,
    Number(ex.currentPfmRevision || ex.pfmRevision || ex.pfmStepVersion || 0),
  );
  // If current run has PFM nodes in DB (ownRev > 0), it's current
  if (mode === "current" || (ownRev > 0 && mode !== "previous")) {
    return resolvePfmMindmapLineage({
      ...ex,
      pfmMapDisplayMode: "current",
      currentRunNumber: currentRn,
      currentPfmRevision: Math.max(
        ownRev,
        Number(ex.currentPfmRevision || ex.pfmRevision || ex.pfmStepVersion || 0),
      ),
    });
  }
  // If current run has committed PFM snapshot (ownRev > 0), treat as current
  if (ownRev > 0 && mode !== "previous") {
    return resolvePfmMindmapLineage({
      ...ex,
      pfmMapDisplayMode: "current",
      currentRunNumber: currentRn,
      currentPfmRevision: Math.max(
        0,
        Number(ex.currentPfmRevision || ex.pfmRevision || ex.pfmStepVersion || 0),
      ),
    });
  }

  const apiV = firstPositiveNumber(
    ex.previousPfmRunNumber,
    ex.pfmSourceRunNumber,
    ex.pfmBaselineTreeVersion,
  );
  const apiRev = firstPositiveNumber(ex.previousPfmRevision);
  if (apiV > 0) {
    return resolvePfmMindmapLineage({
      ...ex,
      pfmMapDisplayMode: "previous",
      pfmSnapshotPhase: ex.pfmSnapshotPhase || "baseline_preview",
      previousPfmRunNumber: apiV,
      previousPfmRevision: apiRev,
      pfmSourceRunNumber: apiV,
      pfmBaselineTreeVersion: apiV,
    });
  }

  const baselineId = String(
    opts?.baselineExecutionId || ex.pfmBaselineExecutionId || ex.pfmTreeReadExecutionId || "",
  ).trim();
  const inhId = String(
    opts?.inheritedExecutionId || ex.inheritedFromExecutionId || "",
  ).trim();

  let pick: PfmLineageRunRef | undefined;
  if (baselineId) {
    pick = runById(templateRuns, baselineId);
  }
  if (!pick && inhId) {
    pick = runById(templateRuns, inhId);
  }
  if (!pick) {
    const readId = String(
      opts?.treeReadExecutionId || ex.pfmTreeReadExecutionId || baselineId || "",
    ).trim();
    if (readId) pick = runById(templateRuns, readId);
  }
  if (!pick) {
    pick = findLatestTemplatePfmTreeRun(templateRuns, selfId);
  }

  const prevRun = Math.max(
    0,
    Number(pick?.runNumber || 0),
    apiV,
  );
  const prevRev = Math.max(
    0,
    Number(pick?.pfmRevision || 0),
    apiRev,
    !pick && apiV > 0 ? firstPositiveNumber(ex.pfmRevision, ex.pfmStepVersion) : 0,
  );

  if (prevRun > 0) {
    return resolvePfmMindmapLineage({
      ...ex,
      pfmMapDisplayMode: "previous",
      pfmSnapshotPhase: ex.pfmSnapshotPhase || "baseline_preview",
      previousPfmRunNumber: prevRun,
      previousPfmRevision: prevRev,
      pfmSourceRunNumber: prevRun,
      pfmBaselineTreeVersion: prevRun,
      pfmBaselineExecutionId: baselineId || pick?.id || ex.pfmBaselineExecutionId,
      pfmTreeReadExecutionId: baselineId || pick?.id || ex.pfmTreeReadExecutionId,
    });
  }

  return resolvePfmMindmapLineage(ex);
}

/** Section/card title: Current vs Most Recent with V xx (Rev yy). */
export function buildPfmMindmapHeaderLabel(
  ex: Parameters<typeof resolveClientPfmLineage>[0],
  templateRuns: PfmLineageRunRef[] = [],
  opts?: Parameters<typeof resolveClientPfmLineage>[2],
): string {
  const lineage = resolveClientPfmLineage(ex, templateRuns, opts);
  const label = formatPfmMindmapDisplayLabel({
    currentRunNumber: lineage.currentRunNumber,
    hasCurrentPfmMap: lineage.hasCurrentPfmMap,
    currentPfmRevision: lineage.currentPfmRevision,
    previousPfmRunNumber: lineage.previousPfmRunNumber,
    previousPfmRevision: lineage.previousPfmRevision,
  });
  if (!/version pending/i.test(label)) return label;
  return formatPfmMindmapHeaderFromExecution(ex);
}

/**
 * Mindmap header: own map vs template's most recent saved map.
 * Examples:
 * - Current EAD Feature Map V 71 (Rev 3)
 * - Most Recent EAD Feature Map V 68 (Rev 8)
 */
export function formatPfmMindmapDisplayLabel(opts: {
  currentRunNumber: number;
  hasCurrentPfmMap: boolean;
  currentPfmRevision?: number;
  previousPfmRunNumber?: number;
  previousPfmRevision?: number;
}): string {
  if (opts.hasCurrentPfmMap) {
    const run = Math.max(0, Number(opts.currentRunNumber || 0));
    const mapLabel = formatPfmMapVersionRev(run, Number(opts.currentPfmRevision || 0));
    return mapLabel
      ? `Current EAD Feature Map ${mapLabel}`
      : "Current EAD Feature Map";
  }
  const prevRun = Math.max(0, Number(opts.previousPfmRunNumber || 0));
  const prevRev = Math.max(0, Number(opts.previousPfmRevision || 0));
  const prevLabel = formatPfmMapVersionRev(prevRun, prevRev);
  if (prevLabel) {
    return `Most Recent EAD Feature Map ${prevLabel}`;
  }
  if (prevRun > 0) {
    return `Most Recent EAD Feature Map V ${prevRun}`;
  }
  return "Most Recent EAD Feature Map (version pending)";
}

/** Resolve header fields from gateway execution row (canonical stored properties). */
export function formatPfmMindmapHeaderFromExecution(ex: {
  runNumber?: number;
  pfmGenerationVersion?: number;
  pfmHasCommittedSnapshot?: boolean;
  pfmMapDisplayMode?: "current" | "previous" | string;
  pfmRevision?: number;
  pfmStepVersion?: number;
  pfmSourceRunNumber?: number;
  pfmBaselineTreeVersion?: number;
  currentRunNumber?: number;
  currentPfmRevision?: number;
  previousPfmRunNumber?: number;
  previousPfmRevision?: number;
}): string {
  const lineage = resolvePfmMindmapLineage(ex);
  return formatPfmMindmapDisplayLabel({
    currentRunNumber: lineage.currentRunNumber,
    hasCurrentPfmMap: lineage.hasCurrentPfmMap,
    currentPfmRevision: lineage.currentPfmRevision,
    previousPfmRunNumber: lineage.previousPfmRunNumber,
    previousPfmRevision: lineage.previousPfmRevision,
  });
}

/** @deprecated Use formatPfmMindmapHeaderFromExecution — kept for narrow call sites. */
export function formatPfmMindmapDisplayLabelLegacy(opts: {
  mode: "current" | "previous";
  runNumber: number;
  revision: number;
}): string {
  const run = Math.max(0, Number(opts.runNumber || 0));
  const rev = Math.max(0, Number(opts.revision || 0));
  if (opts.mode === "current") {
    return formatPfmMindmapDisplayLabel({
      currentRunNumber: run,
      hasCurrentPfmMap: true,
      currentPfmRevision: rev,
    });
  }
  return formatPfmMindmapDisplayLabel({
    currentRunNumber: run,
    hasCurrentPfmMap: false,
    previousPfmRunNumber: run,
    previousPfmRevision: rev,
  });
}
